import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { dashboardSelect } from '@/lib/supabaseHelpers';
import { format } from 'date-fns';
import { ShoppingCart, RefreshCw } from 'lucide-react';

interface ShopifyOrder {
  id: string;
  shopify_order_id: number;
  order_name: string | null;
  order_created_at: string;
  total_price: number;
  currency: string;
  financial_status: string | null;
  variant: 'A' | 'B' | null;
}

interface OrderSummary {
  total_orders: number;
  quiz_attributed_orders: number;
  variant_a: { orders: number; revenue: number };
  variant_b: { orders: number; revenue: number };
  total_quiz_revenue: number;
}

interface LiveShopifyOrdersProps {
  adSpend?: number;
  dateFrom?: Date;
  dateTo?: Date;
}

const LiveShopifyOrders = ({ adSpend, dateFrom, dateTo }: LiveShopifyOrdersProps) => {
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [summary, setSummary] = useState<OrderSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const testStartedAtRef = useRef<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { column: string; operator: string; value: unknown }[] = [];
      const effectiveFrom = testStartedAtRef.current || (dateFrom ? format(dateFrom, 'yyyy-MM-dd') : null);
      if (effectiveFrom) filters.push({ column: 'order_created_at', operator: 'gte', value: effectiveFrom });
      if (dateTo) {
        const end = new Date(dateTo);
        end.setDate(end.getDate() + 1);
        filters.push({ column: 'order_created_at', operator: 'lt', value: format(end, 'yyyy-MM-dd') });
      }
      const rows = await dashboardSelect<ShopifyOrder>(
        'shopify_orders',
        'id, shopify_order_id, order_name, order_created_at, total_price, currency, financial_status, variant',
        { filters, order: { column: 'order_created_at', ascending: false }, limit: 250 },
      );
      setOrders(rows);

      const quizOrders = rows.filter(o => o.variant !== null);
      const variantA = quizOrders.filter(o => o.variant === 'A');
      const variantB = quizOrders.filter(o => o.variant === 'B');
      setSummary({
        total_orders: rows.length,
        quiz_attributed_orders: quizOrders.length,
        variant_a: {
          orders: variantA.length,
          revenue: variantA.reduce((s, o) => s + Number(o.total_price), 0),
        },
        variant_b: {
          orders: variantB.length,
          revenue: variantB.reduce((s, o) => s + Number(o.total_price), 0),
        },
        total_quiz_revenue: quizOrders.reduce((s, o) => s + Number(o.total_price), 0),
      });
    } catch (err) {
      console.error('Failed to fetch Shopify orders:', err);
      setError(err instanceof Error ? err.message : 'Onbekende fout');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    // Fetch active test start date to scope orders
    supabase
      .from('ab_test_log')
      .select('started_at')
      .eq('status', 'active')
      .order('started_at', { ascending: false })
      .limit(1)
      .single()
      .then(({ data }) => {
        testStartedAtRef.current = data?.started_at || null;
        fetchOrders();
      });
    const intervalId = window.setInterval(() => fetchOrders(), 60_000);
    return () => window.clearInterval(intervalId);
  }, [fetchOrders]);

  const roasA = adSpend && summary && summary.variant_a.revenue > 0
    ? (summary.variant_a.revenue / (adSpend * 0.5)) : null;
  const roasB = adSpend && summary && summary.variant_b.revenue > 0
    ? (summary.variant_b.revenue / (adSpend * 0.5)) : null;
  const totalRoas = adSpend && summary && summary.total_quiz_revenue > 0
    ? (summary.total_quiz_revenue / adSpend) : null;

  const quizOrders = orders.filter(o => o.variant !== null);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
            <ShoppingCart className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Shopify Orders & ROAS</h3>
            <p className="text-xs text-gray-400">Via webhook — automatisch bijgewerkt</p>
          </div>
        </div>
        <button onClick={() => fetchOrders()} disabled={loading} className="text-gray-400 hover:text-gray-600 transition-colors">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-xs rounded-lg p-3 mb-4">{error}</div>
      )}

      {loading && !summary ? (
        <div className="text-gray-400 text-sm animate-pulse py-8 text-center">Orders laden...</div>
      ) : summary ? (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">Quiz Orders</p>
              <p className="text-lg font-bold text-gray-800">{summary.quiz_attributed_orders}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">Quiz Omzet</p>
              <p className="text-lg font-bold text-emerald-600">€{summary.total_quiz_revenue.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">ROAS</p>
              <p className="text-lg font-bold text-blue-600">
                {totalRoas ? `${totalRoas.toFixed(2)}x` : '–'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { label: 'Variant A', data: summary.variant_a, roas: roasA, color: 'blue' },
              { label: 'Variant B', data: summary.variant_b, roas: roasB, color: 'purple' },
            ].map(({ label, data, roas, color }) => (
              <div key={label} className={`rounded-xl p-3 border ${color === 'blue' ? 'border-blue-100 bg-blue-50/50' : 'border-purple-100 bg-purple-50/50'}`}>
                <p className={`text-xs font-medium mb-2 ${color === 'blue' ? 'text-blue-600' : 'text-purple-600'}`}>{label}</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Orders</span>
                    <span className="font-semibold text-gray-800">{data.orders}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Omzet</span>
                    <span className="font-semibold text-emerald-600">€{data.revenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">ROAS</span>
                    <span className={`font-semibold ${color === 'blue' ? 'text-blue-600' : 'text-purple-600'}`}>
                      {roas ? `${roas.toFixed(2)}x` : '–'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {quizOrders.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Recente quiz-orders</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {quizOrders.slice(0, 20).map(order => (
                  <div key={order.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${
                        order.variant === 'A' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>{order.variant}</span>
                      <span className="font-medium text-gray-700">{order.order_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400">
                        {order.order_created_at ? format(new Date(order.order_created_at), 'dd/MM HH:mm') : '–'}
                      </span>
                      <span className="font-semibold text-emerald-600">€{Number(order.total_price).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {quizOrders.length === 0 && !loading && (
            <p className="text-xs text-gray-400 text-center py-4">
              Nog geen orders met quiz-attributie gevonden.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
};

export default LiveShopifyOrders;
